import React, { useState } from 'react';
import {
    Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { dsVar, ink } from '../../theme/tokens';

/** Tomorrow, in the yyyy-mm-dd form the date input wants. */
const tomorrow = (): string => {
    const date = new Date();
    date.setDate(date.getDate() + 1);
    return date.toISOString().slice(0, 10);
};

/**
 * Supersede an approved document.
 *
 * The only legitimate way to change one. An approved value may already have governed
 * recorded decisions, so editing it in place would rewrite history; instead the current
 * version is closed and a successor is staged. The unique indexes are on
 * (code, orgId, effectiveFrom) precisely so both versions coexist, and an auditor asking
 * "what threshold applied in March" still gets the March answer.
 *
 * The successor is created as a DRAFT, so between this call and its approval nothing is
 * in force for the code. Defaulting to tomorrow avoids that gap; the dialog says so
 * rather than letting the user discover it from a warning after the fact.
 */
const SupersedeDialog: React.FC<{
    open: boolean;
    code: string;
    submitting?: boolean;
    onClose: () => void;
    onConfirm: (effectiveFrom: string) => void;
}> = ({ open, code, submitting, onClose, onConfirm }) => {
    const [effectiveFrom, setEffectiveFrom] = useState(tomorrow());

    const isImmediate = !effectiveFrom || new Date(effectiveFrom) <= new Date();

    return (
        <Dialog
            open={open}
            onClose={submitting ? undefined : onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{ sx: { backgroundColor: dsVar('surface'), border: `1px solid ${dsVar('line')}`, backgroundImage: 'none' } }}
        >
            <DialogTitle sx={{ color: dsVar('fg'), fontSize: '1rem', fontWeight: 600 }}>
                Supersede this version
            </DialogTitle>

            <DialogContent>
                <Box
                    sx={{
                        display: 'flex', gap: 1.25, alignItems: 'flex-start',
                        p: 1.5, mb: 2.5, borderRadius: '6px',
                        backgroundColor: 'rgba(125, 211, 252, 0.08)',
                        border: '1px solid rgba(125, 211, 252, 0.3)',
                    }}
                >
                    <InfoOutlinedIcon sx={{ fontSize: 20, color: ink('#7DD3FC'), mt: '1px', flexShrink: 0 }} />
                    <Typography sx={{ fontSize: '0.8125rem', color: ink('#BAE6FD'), lineHeight: 1.6 }}>
                        The current version stays readable — it is closed, not deleted, so the value
                        that governed past decisions remains on record. A new draft is created for
                        you to edit and approve.
                    </Typography>
                </Box>

                <Typography
                    sx={{
                        fontSize: '0.6875rem', fontWeight: 600, letterSpacing: '0.05em',
                        textTransform: 'uppercase', color: dsVar('fg-muted'), mb: 0.5,
                    }}
                >
                    Successor effective from
                </Typography>
                <TextField
                    type="date"
                    size="small"
                    fullWidth
                    value={effectiveFrom}
                    onChange={(e) => setEffectiveFrom(e.target.value)}
                    sx={{
                        mb: 1.5,
                        '& .MuiOutlinedInput-root': {
                            backgroundColor: dsVar('page'), color: dsVar('fg-body'), fontSize: '0.875rem',
                            '& fieldset': { borderColor: dsVar('line') },
                            '&.Mui-focused fieldset': { borderColor: dsVar('accent-fg') },
                        },
                    }}
                />

                {isImmediate && (
                    <Box
                        sx={{
                            display: 'flex', gap: 1.25, alignItems: 'flex-start',
                            p: 1.25, borderRadius: '6px',
                            backgroundColor: 'rgba(251, 191, 36, 0.08)',
                            border: '1px solid rgba(251, 191, 36, 0.35)',
                        }}
                    >
                        <WarningAmberIcon sx={{ fontSize: 18, color: ink('#FBBF24'), mt: '1px', flexShrink: 0 }} />
                        <Typography sx={{ fontSize: '0.8125rem', color: ink('#FCD34D'), lineHeight: 1.6 }}>
                            With a date of today or earlier, <strong>{code}</strong> is closed
                            immediately while its successor is still a draft — so nothing will be in
                            force for it until you approve that draft. Pick a future date to avoid
                            the gap.
                        </Typography>
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2.5 }}>
                <Button onClick={onClose} disabled={submitting} sx={{ color: dsVar('fg-2'), textTransform: 'none' }}>
                    Cancel
                </Button>
                <Button
                    onClick={() => onConfirm(effectiveFrom)}
                    disabled={submitting}
                    sx={{
                        textTransform: 'none', fontWeight: 600,
                        backgroundColor: dsVar('accent'), color: dsVar('on-accent'),
                        '&:hover': { backgroundColor: '#F5D149' },
                        '&.Mui-disabled': { backgroundColor: dsVar('line'), color: dsVar('fg-muted') },
                    }}
                >
                    {submitting ? 'Superseding…' : 'Create successor draft'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default SupersedeDialog;
